import { CyclingEvent, EventEditResponse } from './types';
import { canonicalLinkUrl } from './utils';

/**
 * Das Event so, wie es mit dem offenen Vorschlag des Einreichenden aussähe. Das Bearbeiten-Formular
 * setzt hier an statt beim Event: der neue Vorschlag ersetzt den offenen (Backend,
 * `supersedeOwnOpenEdits`) und muss deshalb alles enthalten, was der offene schon geändert hat.
 *
 * Anlass war der 02.10.2026: ein Verein schickte zu einem Event zehn Vorschläge, jeder begann
 * wieder beim Stand des Events, und die Teilnehmerzahl aus dem dritten fehlte in allen späteren.
 *
 * Was ein Vorschlag nicht trägt (Bild, Flyer-Upload, interne Links, Status), bleibt vom Event.
 * Felder, die ein Vorschlag leer lässt, heißen dort „keine Angabe" und löschen beim Freigeben
 * nichts; deshalb füllt sie hier ebenfalls das Event.
 */
export function eventWithOpenProposal(event: CyclingEvent, edit: EventEditResponse): CyclingEvent {
  const proposedUrls = new Set((edit.links ?? []).map(l => canonicalLinkUrl(l.url)));
  return {
    ...event,
    title: edit.title,
    description: edit.description ?? event.description,
    locationName: edit.locationName,
    street: edit.street,
    houseNumber: edit.houseNumber,
    postalCode: edit.postalCode,
    city: edit.city,
    bundesland: edit.bundesland,
    eventDate: edit.eventDate,
    endDate: edit.endDate,
    startTime: edit.startTime,
    startTimeEnd: edit.startTimeEnd ?? event.startTimeEnd,
    participants: edit.participants ?? event.participants,
    organizer: edit.organizer ?? event.organizer,
    organizerId: edit.organizerId ?? event.organizerId,
    category: edit.category,
    secondaryCategories: edit.secondaryCategories ?? event.secondaryCategories,
    eventType: edit.eventType ?? event.eventType,
    organizerUrl: edit.organizerUrl ?? event.organizerUrl,
    flyerUrlExternal: edit.flyerUrl ?? event.flyerUrlExternal,
    imageCredit: edit.imageCredit ?? event.imageCredit,
    fullyBooked: edit.fullyBooked,
    cancelled: edit.cancelled,
    tentative: edit.tentative,
    permanent: edit.permanent ?? event.permanent,
    seasonFrom: edit.permanent != null ? edit.seasonFrom : event.seasonFrom,
    seasonUntil: edit.permanent != null ? edit.seasonUntil : event.seasonUntil,
    areaName: edit.permanent != null ? edit.areaName : event.areaName,
    registrationStart: edit.registrationStart ?? event.registrationStart,
    registrationMode: edit.registrationMode && edit.registrationMode !== 'UNKNOWN'
      ? edit.registrationMode : event.registrationMode,
    registrationState: edit.registrationState && edit.registrationState !== 'UNKNOWN'
      ? edit.registrationState : event.registrationState,
    // Ohne `id`: die Strecken des Vorschlags sind noch keine Strecken des Events. Ein GPX am Event
    // hängt an dessen Strecke und bleibt beim Freigeben über den Abgleich nach km erhalten.
    distances: edit.distances.map(d => ({ ...d, id: undefined as unknown as number, hasGpx: false })),
    // Beim Freigeben kommen Links nur hinzu, also hier: die des Events plus die vorgeschlagenen.
    links: [
      ...(event.links ?? []).filter(l => !proposedUrls.has(canonicalLinkUrl(l.url))),
      ...(edit.links ?? []),
    ],
    priceTiers: edit.priceTiers ?? event.priceTiers,
  };
}
